import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, Plus, Tag as TagIcon, Users } from "lucide-react";
import { toast } from "sonner";
import * as tagsApi from "@/services/tagsApi";


interface Props {
  selectedTagIds: string[];
  onChange: (ids: string[]) => void;
}

export function ScopeStep({ selectedTagIds, onChange }: Props) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("");
  const [newName, setNewName] = useState("");

  const tagsQuery = useQuery({ queryKey: ["tags"], queryFn: () => tagsApi.listTags(), staleTime: 30_000 });
  const tags = tagsQuery.data ?? [];

  const filteredTags = useMemo(
    () =>
      tags.filter((t) => {
        const name = String((t as { tag_name?: string; name?: string }).tag_name ?? (t as { name?: string }).name ?? "");
        return name.toLowerCase().includes(filter.toLowerCase());
      }),
    [tags, filter],
  );

  const createTag = useMutation({
    mutationFn: async (name: string) => {
      await tagsApi.createTagForTenants([], {
        name,
        description: "",
        type: "static",
        color: "#3B82F6",
        icon: null,
      });
    },
    onSuccess: async () => {
      toast.success("Tag created");
      setNewName("");
      await qc.invalidateQueries({ queryKey: ["tags"] });
    },
    onError: () => toast.error("Failed to create tag"),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TagIcon className="h-5 w-5 text-primary" aria-hidden="true" /> Scope (tags)
        </CardTitle>
        <CardDescription>
          A patching policy reaches devices through tags. Devices and the policy must share a tag.
          You can pick one or more existing tags, or create a new static tag below.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-2">
          <Label htmlFor="tag-filter">Find a tag</Label>
          <Input id="tag-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by name…" />
        </div>

        {tagsQuery.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : filteredTags.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tags found.</p>
        ) : (
          <ul className="space-y-2 max-h-80 overflow-auto rounded-md border p-2">
            {filteredTags.map((t) => {
              const id = String((t as { tag_id?: string; id?: string }).tag_id ?? (t as { id?: string }).id ?? "");
              const name = String((t as { tag_name?: string; name?: string }).tag_name ?? (t as { name?: string }).name ?? "");
              if (!id) return null;
              const checked = selectedTagIds.includes(id);
              return (
                <li key={id}>
                  <label
                    className={`flex items-center justify-between gap-3 rounded-md border p-2 cursor-pointer transition-colors ${
                      checked ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) =>
                          onChange(
                            e.target.checked
                              ? [...selectedTagIds, id]
                              : selectedTagIds.filter((x) => x !== id),
                          )
                        }
                        className="h-4 w-4"
                        aria-label={`Select tag ${name}`}
                      />
                      <span className="text-sm">{name}</span>
                    </span>
                    {checked && <TagDeviceCount tagId={id} />}
                  </label>
                </li>
              );
            })}
          </ul>
        )}

        <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Create a new static tag…"
          />
          <Button
            type="button"
            variant="outline"
            disabled={!newName.trim() || createTag.isPending}
            onClick={() => createTag.mutate(newName.trim())}
          >
            {createTag.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-1" />
            )}
            Create tag
          </Button>
        </div>

        {selectedTagIds.length > 0 && (
          <div className="rounded-md border p-3">
            <div className="text-xs font-medium text-muted-foreground mb-2">Will apply to devices tagged with</div>
            <div className="flex flex-wrap gap-1.5">
              {selectedTagIds.map((id) => {
                const t = tags.find((x) => String((x as { tag_id?: string; id?: string }).tag_id ?? (x as { id?: string }).id) === id);
                const name = t ? String((t as { tag_name?: string; name?: string }).tag_name ?? (t as { name?: string }).name) : id;
                return (
                  <Badge key={id} variant="secondary" className="font-normal">
                    {name}
                  </Badge>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TagDeviceCount({ tagId }: { tagId: string }) {
  const q = useQuery({
    queryKey: ["tag-devices", tagId],
    queryFn: () => tagsApi.getDevicesByTag(tagId),
    staleTime: 60_000,
  });
  const count = q.data?.length ?? 0;
  return (
    <span className="text-xs text-muted-foreground flex items-center gap-1">
      <Users className="h-3 w-3" aria-hidden="true" /> {q.isLoading ? "…" : count} device{count === 1 ? "" : "s"}
    </span>
  );
}
